import { useEffect, useState } from "react";
import { ExternalLink, Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Channel-Setup-Flow (docs/research/channel-setup-flow.md §3.1): GENERISCHER
// Renderer über das kleine SDUI-Vokabular. Er kennt nur die Element-Typen,
// nie die Channel-Semantik — und führt NIEMALS Plugin-Code aus.
//
// Sanitizing (Konzept §11.5, nicht verhandelbar):
//   - Bilder/QR: NUR Raster-data-URIs (png/jpeg base64). KEIN SVG (kann JS tragen).
//   - Links/open-url: NUR https://.
//   - Text: reiner Text (React escapt), kein HTML/dangerouslySetInnerHTML.
// Alles was nicht ins Vokabular passt, wird still übersprungen (Escape-Hatch:
// rohes Channel.config-JSON via UpdateChannelDialog).

const SAFE_IMAGE_DATA_URI = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/;
const SAFE_HREF = /^https:\/\/\S+$/;

export interface SetupActionPayload {
  actionId: string;
  stepId?: string;
  values?: Record<string, unknown>;
}

interface SetupElement {
  type?: unknown;
  [k: string]: unknown;
}
interface SetupStep {
  stepId?: unknown;
  title?: unknown;
  elements?: unknown;
  actions?: unknown;
}
interface SetupSchema {
  state?: unknown;
  step?: SetupStep;
}

function asString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

export function SetupRenderer({
  schema,
  onAction,
  isPending,
}: {
  schema: unknown;
  onAction: (action: SetupActionPayload) => void;
  isPending?: boolean;
}) {
  const step = (schema as SetupSchema | null)?.step;
  const stepId = asString(step?.stepId) ?? undefined;
  // Eingabewerte lokal, pro Schritt — Schritt-Wechsel (neues stepId) leert das Formular.
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    setValues({});
  }, [stepId]);

  if (!step || !Array.isArray(step.elements)) {
    return <div className="text-[13px] text-text3">Das Plugin hat noch kein Setup-Schema gemeldet.</div>;
  }

  const elements = step.elements as SetupElement[];
  const actions = Array.isArray(step.actions) ? (step.actions as SetupElement[]) : [];
  const title = asString(step.title);

  function submit(actionId: string) {
    onAction({ actionId, stepId, values: Object.keys(values).length > 0 ? values : undefined });
  }

  return (
    <div className="space-y-4" data-testid="setup-renderer">
      {title && <div className="text-[14px] font-semibold text-text">{title}</div>}

      {elements.map((el, i) => {
        const type = asString(el.type);
        switch (type) {
          case "text": {
            const value = asString(el.value);
            if (!value) return null;
            return (
              <p key={i} className="whitespace-pre-wrap text-[13px] leading-relaxed text-text2" data-testid="setup-el-text">
                {value}
              </p>
            );
          }
          case "qr":
          case "image": {
            const dataUri = asString(el.dataUri);
            if (!dataUri || !SAFE_IMAGE_DATA_URI.test(dataUri)) return null; // Sanitizing: nur Raster
            return (
              <div key={i} className="flex justify-center rounded-[10px] border border-border bg-white p-3">
                <img
                  src={dataUri}
                  alt={asString(el.alt) ?? "Setup-Bild"}
                  className="max-h-[320px] max-w-full"
                  data-testid="setup-el-image"
                />
              </div>
            );
          }
          case "status": {
            const state = asString(el.state) ?? "waiting";
            const label = asString(el.label) ?? state;
            const tone =
              state === "ok"
                ? { cls: "border-ok-border bg-ok-bg text-ok", Icon: CheckCircle2 }
                : state === "error"
                  ? { cls: "border-danger-border bg-danger-bg text-danger", Icon: AlertTriangle }
                  : { cls: "border-warn-border bg-warn-bg text-warn", Icon: Clock };
            return (
              <div
                key={i}
                className={`flex items-center gap-2 rounded-[10px] border px-3 py-2 text-[13px] font-medium ${tone.cls}`}
                data-testid="setup-el-status"
              >
                <tone.Icon className="h-4 w-4 shrink-0" />
                {label}
              </div>
            );
          }
          case "input": {
            const key = asString(el.key);
            const label = asString(el.label) ?? key;
            if (!key) return null;
            const kind = asString(el.kind) ?? "text";
            const options = Array.isArray(el.options) ? (el.options as unknown[]).filter((o) => typeof o === "string") as string[] : [];
            return (
              <div key={i} className="space-y-1.5" data-testid="setup-el-input">
                <Label htmlFor={`setup-input-${key}`}>{label}</Label>
                {kind === "select" ? (
                  <select
                    id={`setup-input-${key}`}
                    className="h-9 w-full rounded-[6px] border border-border bg-surface px-3 text-[13px] text-text"
                    value={values[key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  >
                    <option value="">— wählen —</option>
                    {options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={`setup-input-${key}`}
                    type={kind === "secret" ? "password" : "text"}
                    autoComplete="off"
                    value={values[key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                )}
              </div>
            );
          }
          case "link": {
            const href = asString(el.href);
            if (!href || !SAFE_HREF.test(href)) return null; // Sanitizing: nur https
            return (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-accent-text hover:underline"
                data-testid="setup-el-link"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {asString(el.label) ?? href}
              </a>
            );
          }
          default:
            return null; // unbekanntes Element → still überspringen (Vokabular ist fix)
        }
      })}

      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="setup-actions">
          {actions.map((a, i) => {
            const id = asString(a.id);
            const label = asString(a.label) ?? id;
            const kind = asString(a.kind);
            if (!id) return null;
            if (kind === "open-url") {
              const href = asString(a.href);
              if (!href || !SAFE_HREF.test(href)) return null; // Sanitizing: nur https
              return (
                <Button key={i} variant="outline" size="sm" asChild data-testid={`setup-action-${id}`}>
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    {label}
                  </a>
                </Button>
              );
            }
            if (kind !== "submit") return null;
            return (
              <Button
                key={i}
                variant="outline"
                size="sm"
                disabled={isPending}
                onClick={() => submit(id)}
                data-testid={`setup-action-${id}`}
              >
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
